import { createContext, useEffect, useState } from "react";

type Theme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  themeStyles: Record<string, any>;
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    return (localStorage.getItem("rd-rental-theme") as Theme) || "light";
  });
  const [themeStyles, setThemeStyles] = useState<Record<string, any>>({});

  const getThemeStyles = (theme: Theme) => ({
    main: {
      tailwindSectionBg: theme === "dark" ? "bg-[#101828]" : "",
      mainBg: theme === "dark" ? "#171f2f" : "white",
      tailwindMainBg: theme === "dark" ? "bg-[#171f2f]" : "bg-white",
      borderColor: theme === "dark" ? "border-[#404954]" : "border-[#E4E7EC]",
      textHeadingColor: theme === "dark" ? "text-[#E4E7EC]" : "text-[#1D2939]",
      textSubHeadingColor: theme === "dark" ? "text-[#98a2b3]" : "text-black-rgba",
      iconColor: theme === "dark" ? "#98a2b3" : "rgba(0, 0, 0, 0.54)",
    },
    sideBar: {
      bg: theme === "dark" ? "#101828" : "#ffffff",
      tailwindBg: theme === "dark" ? "bg-[#101828]" : "bg-white",
      borderColor: theme === "dark" ? "#404954" : "#D0D0D0",
      tailwindBorderColor: theme === "dark" ? "border-[#404954]" : "border-[#D0D0D0]",
      menuItemsColor: theme === "dark" ? "#E4E7EC" : "rgba(0, 0, 0, 0.54)",
      tailwindMenuItemsColor: theme === "dark" ? "text-[#E4E7EC]" : "text-black-rgba",
      menuItemsHoverColor: theme === "dark" ? "#253045" : "#eeeeee",
      tailwindMenuItemsHoverColor: theme === "dark" ? "hover:bg-[#253045]" : "hover:bg-[#eeeeee]",
      subMenuHeading: theme === "dark" ? "text-[#FFFFFF]" : "text-[#000000]",
    },
    topBar: {
      bg: theme === "dark" ? "#101828" : "#ffffff",
      borderColor: theme === "dark" ? "#404954" : "#D0D0D0",
      tailwindBorderColor: theme === "dark" ? "border-[#404954]" : "border-[#D0D0D0]",
      tailwindBg: theme === "dark" ? "bg-[#101828]" : "bg-white",
      textColor: theme === "dark" ? "#E4E7EC" : "rgba(0, 0, 0, 0.54)",
      tailwindTextColor: theme === "dark" ? "text-[#E4E7EC]" : "text-black-rgba",
      menuItemsHoverColor: theme === "dark" ? "#253045" : "#eeeeee",
    },
    divider: {
      bg: theme === "dark" ? "bg-[#737373]" : "",
    },
    tabs: {
      bg: theme === "dark" ? "bg-[#253045]" : "bg-[#dcdcdd]",
      btnBg: theme === "dark" ? "bg-[#101828]" : "bg-white",
      textActiveTextColor: theme === "dark" ? "text-[#F6F6F6]" : "text-[#1D2939]",
      textInActiveTextColor: theme === "dark" ? "text-[#B4B4B4]" : "text-gray-500",
    },
    datePicker: {
      bg: theme === "dark" ? "#101828" : "#ffffff",
      rangeBg: theme === "dark" ? "#313b4f" : "white",
      textHeadingColor: theme === "dark" ? "#E4E7EC" : "#1D2939",
      textSubHeadingColor: theme === "dark" ? "#98a2b3" : "rgba(0, 0, 0, 0.54)",
    },
    table: {
      headerBg: theme === "dark" ? "bg-[#101828]" : "bg-[#F9FAFB]",
      headerColor: theme === "dark" ? "#98a2b3" : "rgba(0, 0, 0, 0.54)",
      tailwindHeaderColor: theme === "dark" ? "text-[#98a2b3]" : "text-black-rgba",
      textColor: theme === "dark" ? "#E4E7EC" : "#1D2939",
      tailwindTextColor: theme === "dark" ? "text-[#E4E7EC]" : "text-[#1D2939]",
    },
    form: {
      borderColor: theme === "dark" ? "#404954" : "#D0D0D0",
      tailwindBorderColor: theme === "dark" ? "border-[#404954]" : "border-[#D0D0D0]",
      labelTextColor: theme === "dark" ? "#98a2b3" : "#667085",
      tailwindLabelTextColor: theme === "dark" ? "text-[#98a2b3]" : "text-[#667085]",
      inputTextColor: theme === "dark" ? "#E4E7EC" : "#1D2939",
      tailwindInputTextColor: theme === "dark" ? "text-[#E4E7EC]" : "text-[#1D2939]",
    },
    editor: {
      bg: theme === "dark" ? "#101828" : "#ffffff",
      color: theme === "dark" ? "#ffffff" : "#101828",
    },
    calendar: {
      calendarBgSecondary: theme === "dark" ? "#333f56" : "rgb(255, 254, 224, 0.5)",
      calendarIsChildBg: theme === "dark" ? "#444b6680" : "rgba(236, 239, 241, 0.5)",
      calendarVehicleIsChildBg: theme === "dark" ? "#373b50" : "#ECEFF1",
      calendarRepeatedLinearBg: theme === "dark" ? "repeating-linear-gradient(-45deg, #3b4a66, #3b4a66 2px, #2a3347 2px, #2a3347 4px)" : "repeating-linear-gradient(-45deg, #FFFEE0, #FFFEE0 2px, #f3f3f5 2px, #f3f3f5 4px)",
    }
  });

  useEffect(() => {
    setThemeStyles(getThemeStyles(theme));
    localStorage.setItem("rd-rental-theme", theme);

    const root = document.documentElement;
    root.style.setProperty("--app-link-color", theme === "dark" ? "#66B2FF" : "#0000ff");
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, themeStyles }}>
      {children}
    </ThemeContext.Provider>
  );
};